import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useAuthStore } from '../contexts/authStore';
import { useMeetingStore } from '../contexts/meetingStore';
import { useVideoEffectsStore, connectAuthStore } from '../contexts/videoEffectsStore';
import { useVideoEffects } from '../hooks/useVideoEffects';
import { initSocket, getSocket, disconnectSocket, emitAsync } from '../services/socket';
import * as mediasoup from '../services/mediasoup';
import { startAudioCapture, stopAudioCapture, playTranslatedAudio, stopPlayback, initPlayback } from '../services/audioCapture';
import { startAudioStreamProcessing, stopAudioStreamProcessing, isAudioProcessingActive, getProcessingStatus } from '../services/audioStreamProcessor';
import { startSTTProcessing, stopSTTProcessing, isSTTProcessingActive } from '../services/sttStreamProcessor';
import MeetingControls from '../components/meeting/MeetingControls';
import VideoGrid from '../components/meeting/VideoGrid';
import ChatPanel from '../components/meeting/ChatPanel';
import ParticipantsPanel from '../components/meeting/ParticipantsPanel';
import WaitingRoom from '../components/meeting/WaitingRoom';
import SettingsDialog from '../components/meeting/SettingsDialog';
import SubtitlesPanel from '../components/meeting/SubtitlesPanel';
import VideoEffectsPanel from '../components/meeting/VideoEffectsPanel';

export default function MeetingPage() {
  const { meetingCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const authStore = useAuthStore();
  const { user, token, isAuthenticated } = authStore;
  const store = useMeetingStore();

  // Video effects
  const {
    effectType,
    effectsPanelOpen,
    setEffectsPanelOpen,
  } = useVideoEffectsStore();

  const [connecting, setConnecting] = useState(true);
  const [error, setError] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState(new Map());
  const [noiseSuppressionActive, setNoiseSuppressionActive] = useState(false);
  const [vadActive, setVadActive] = useState(false);

  const localStreamRef = useRef(null);
  const audioProducerRef = useRef(null);
  const videoProducerRef = useRef(null);

  // Video effects processing
  const { getOutputTrack, getOutputStream, isProcessing: effectsProcessing } = useVideoEffects(
    localStream,
    effectType !== 'none' && store.videoEnabled
  );

  // Connect auth store for video effects syncing
  useEffect(() => {
    connectAuthStore(authStore);
  }, [authStore]);

  // Handle translation audio capture
  useEffect(() => {
    if (store.translationEnabled && localStreamRef.current) {
      const socket = getSocket();
      if (socket) {
        startAudioCapture(localStreamRef.current, socket).catch(err => {
          console.error('[MeetingPage] Failed to start audio capture:', err);
        });
      }
    } else {
      stopAudioCapture();
    }
  }, [store.translationEnabled]);

  // Replace video track when effects processing starts
  // IMPORTANT: We NEVER replace back to the camera track because mediasoup.replaceTrack
  // ends the old track, which would stop the camera. Instead, the processor runs in
  // "passthrough" mode when effects are disabled (effectType === 'none').
  const hasReplacedWithProcessed = useRef(false);

  useEffect(() => {
    const replaceTrack = async () => {
      if (!videoProducerRef.current || !localStreamRef.current) return;

      // Only replace once - after that the processor handles everything
      if (hasReplacedWithProcessed.current) {
        console.log('[MeetingPage] Already using processed track, skipping replace');
        return;
      }

      // Wait for processor to generate frames
      await new Promise(resolve => setTimeout(resolve, 500));

      const newTrack = getOutputTrack();
      if (!newTrack) {
        console.warn('[MeetingPage] No output track available for effects');
        return;
      }

      // Verify track is live
      if (newTrack.readyState !== 'live') {
        console.warn('[MeetingPage] Output track not live, skipping replace');
        return;
      }

      try {
        await mediasoup.replaceVideoTrack(newTrack);
        hasReplacedWithProcessed.current = true;
        console.log('[MeetingPage] Video track replaced with processed track (one-time)');
      } catch (err) {
        console.error('[MeetingPage] Failed to replace video track:', err);
      }
    };

    // Replace track when processing starts (only once)
    if (store.videoEnabled && effectsProcessing && !hasReplacedWithProcessed.current) {
      replaceTrack();
    }
  }, [effectsProcessing, store.videoEnabled]);

  // Redirect to join page if guest hasn't entered their info
  useEffect(() => {
    // If not authenticated and no guest info in state, redirect to join page
    if (!isAuthenticated && !location.state?.guestName) {
      navigate(`/${meetingCode}`, { replace: true });
    }
  }, [isAuthenticated, location.state, meetingCode, navigate]);

  // Initialize socket and join room
  useEffect(() => {
    // Don't initialize if we're about to redirect
    if (!isAuthenticated && !location.state?.guestName) {
      return;
    }

    let mounted = true;

    const init = async () => {
      try {
        // Check if mediaDevices is available (requires secure context)
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
          if (!isSecure) {
            throw new Error('Camera/microphone access requires HTTPS or localhost. Please access via http://localhost:3003');
          } else {
            throw new Error('Your browser does not support camera/microphone access');
          }
        }

        // Get user media with HD resolution (1080p)
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: true,
        });

        if (!mounted) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        // Process audio through noise suppression (applies to WebRTC and STT)
        console.log('[MeetingPage] Applying AI noise suppression to audio stream...');
        let processedStream;
        try {
          processedStream = await startAudioStreamProcessing(stream);
          const nsActive = isAudioProcessingActive();
          setNoiseSuppressionActive(nsActive);
          console.log('[MeetingPage] Audio noise suppression:', nsActive ? 'ACTIVE' : 'inactive');
        } catch (nsError) {
          console.warn('[MeetingPage] Noise suppression failed, using original stream:', nsError);
          processedStream = stream;
          setNoiseSuppressionActive(false);
        }

        localStreamRef.current = processedStream;
        setLocalStream(processedStream);

        // Start STT processing (always active - includes VAD for speaking indicator)
        // Use RNNoise-processed stream for VAD and STT
        const userLanguage = user?.preferredLanguage || 'en';
        console.log('[MeetingPage] Starting STT processing with language:', userLanguage);

        // Update store to show loading state
        useMeetingStore.getState().setSTTLoading(true);

        try {
          await startSTTProcessing(processedStream, {
            language: userLanguage,
            onLoadingProgress: (progress, status) => {
              console.log(`[MeetingPage] STT model loading: ${progress}%`);
              useMeetingStore.getState().setSTTLoadingProgress(progress);
            },
            onReady: ({ vadReady, sttReady }) => {
              console.log(`[MeetingPage] VAD ready: ${vadReady}, STT ready: ${sttReady}`);
              if (vadReady) {
                setVadActive(true); // VAD calibrated - video border glow works
              }
              if (sttReady) {
                useMeetingStore.getState().setSTTLoading(false);
                useMeetingStore.getState().setSTTActive(true);
              }
            },
            onSpeechStart: () => {
              // VAD detected speech start
            },
            onSpeechEnd: () => {
              // VAD detected speech end - transcription will follow
            },
            onActiveSpeaker: () => {
              // Update active speaker for video border glow
              const currentPeerId = useMeetingStore.getState().participant?.peerId || 'local';
              useMeetingStore.getState().setActiveSpeaker(currentPeerId);
            },
            onTranscription: (result) => {
              // Add local transcription to store
              const currentPeerId = useMeetingStore.getState().participant?.peerId || 'local';
              useMeetingStore.getState().addLocalTranscript({
                peerId: currentPeerId,
                text: result.text,
                utteranceId: result.utteranceId,
              });
              console.log(`[MeetingPage] Transcription: "${result.text}"`);
            },
          });
          console.log('[MeetingPage] STT processing started');
        } catch (sttError) {
          console.warn('[MeetingPage] STT processing failed:', sttError);
          useMeetingStore.getState().setSTTLoading(false);
          useMeetingStore.getState().setSTTActive(false);
          setVadActive(false);
        }

        // Monitor camera track state
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) {
          videoTrack.onended = () => {
            console.error('[MeetingPage] CAMERA TRACK ENDED! readyState:', videoTrack.readyState);
            console.trace('[MeetingPage] Track ended stack trace');
          };
          videoTrack.onmute = () => {
            console.warn('[MeetingPage] Camera track muted');
          };
          console.log('[MeetingPage] Camera track monitoring enabled, id:', videoTrack.id);
        }

        // Get audio/video state from sessionStorage (set by JoinPage) or fallback to store
        const savedState = sessionStorage.getItem('meetingMediaState');
        let initialAudioEnabled = true;
        let initialVideoEnabled = true;

        if (savedState) {
          const parsed = JSON.parse(savedState);
          initialAudioEnabled = parsed.audioEnabled;
          initialVideoEnabled = parsed.videoEnabled;
          sessionStorage.removeItem('meetingMediaState'); // Clean up
          console.log('[MeetingPage] Using sessionStorage state - audio:', initialAudioEnabled, 'video:', initialVideoEnabled);
        } else {
          const currentState = useMeetingStore.getState();
          initialAudioEnabled = currentState.audioEnabled;
          initialVideoEnabled = currentState.videoEnabled;
          console.log('[MeetingPage] Using store state - audio:', initialAudioEnabled, 'video:', initialVideoEnabled);
        }

        // Apply initial mute states to tracks
        stream.getAudioTracks().forEach((t) => (t.enabled = initialAudioEnabled));
        stream.getVideoTracks().forEach((t) => (t.enabled = initialVideoEnabled));

        // Sync store with actual track state
        store.setAudioEnabled(initialAudioEnabled);
        store.setVideoEnabled(initialVideoEnabled);

        // Initialize socket
        const socket = initSocket(token);

        // Setup socket event listeners
        setupSocketListeners(socket);

        // Join room
        const guestName = location.state?.guestName;
        const guestGender = location.state?.guestGender;

        const joinData = {
          roomCode: meetingCode,
          displayName: isAuthenticated ? user.displayName : guestName || 'Guest',
          gender: isAuthenticated ? user.gender : guestGender || 'male',
          userId: isAuthenticated ? user.id : null,
          audioEnabled: initialAudioEnabled,
          videoEnabled: initialVideoEnabled,
        };
        console.log('[MeetingPage] Joining with audioEnabled:', initialAudioEnabled, 'videoEnabled:', initialVideoEnabled);

        const result = await emitAsync('join-room', joinData);

        if (!mounted) return;

        if (result.status === 'waiting') {
          store.setStatus('waiting');
          store.setParticipant(result.participant);
        } else {
          store.setStatus('joined');
          store.setMeeting(result.meeting);
          store.setParticipant(result.participant);
          store.setIsHost(result.meeting.isHost);
          store.setParticipants(result.participants || []);

          // Pre-initialize audio playback context (needs user interaction)
          initPlayback();

          // Setup mediasoup
          await setupMediasoup(stream);

          // Sync initial audio/video state with server (fixes state mismatch on join)
          const socket = getSocket();
          if (socket && !initialAudioEnabled) {
            socket.emit('toggle-audio', { enabled: false });
          }
          if (socket && !initialVideoEnabled) {
            socket.emit('toggle-video', { enabled: false });
          }
        }

        setConnecting(false);
      } catch (err) {
        console.error('Failed to join meeting:', err);
        if (mounted) {
          setError(err.message);
          setConnecting(false);
        }
      }
    };

    init();

    return () => {
      mounted = false;
      cleanup();
    };
  }, [meetingCode]);

  const setupSocketListeners = (socket) => {
    socket.on('admitted', async (data) => {
      console.log('[MeetingPage] Admitted to meeting:', data);
      store.setStatus('joined');
      store.setMeeting(data.meeting);
      store.setIsHost(data.meeting?.isHost || false);
      store.setParticipants(data.participants || []);

      // Initialize audio playback context (needed for Web Audio API)
      initPlayback();

      if (localStreamRef.current) {
        await setupMediasoup(localStreamRef.current);

        // Sync initial audio/video state with server (tracks were set up during waiting)
        const currentAudioEnabled = store.audioEnabled;
        const currentVideoEnabled = store.videoEnabled;
        console.log('[MeetingPage] Syncing state after admission - audio:', currentAudioEnabled, 'video:', currentVideoEnabled);

        if (!currentAudioEnabled) {
          socket.emit('toggle-audio', { enabled: false });
        }
        if (!currentVideoEnabled) {
          socket.emit('toggle-video', { enabled: false });
        }
      }
    });

    socket.on('rejected', () => {
      setError('You were not admitted to the meeting');
      store.setStatus('rejected');
    });

    socket.on('participant-joined', ({ participant }) => {
      console.log('participant-joined event received:', participant);
      console.log('participant-joined videoEnabled:', participant.videoEnabled, 'audioEnabled:', participant.audioEnabled);
      store.addParticipant(participant);
    });

    socket.on('participant-left', ({ socketId }) => {
      store.removeParticipant(socketId);
      mediasoup.closeAllConsumersForPeer(socketId);
      setRemoteStreams((prev) => {
        const next = new Map(prev);
        next.delete(socketId);
        return next;
      });
    });

    socket.on('participant-updated', (update) => {
      store.updateParticipant(update.socketId, update);
    });

    socket.on('waiting-room-update', ({ waitingList }) => {
      console.log('[MeetingPage] waiting-room-update received:', waitingList);
      store.setWaitingList(waitingList);
    });

    socket.on('new-producer', async ({ producerId, peerId, kind }) => {
      try {
        const consumer = await mediasoup.consume(producerId);
        addRemoteTrack(peerId, consumer.track, kind);
      } catch (err) {
        console.error('Failed to consume:', err);
      }
    });

    socket.on('new-message', (message) => {
      store.addMessage(message);
    });

    socket.on('transcript', ({ peerId, text, type, utteranceId }) => {
      store.addTranscript({ peerId, text, type, utteranceId, timestamp: Date.now() });
    });

    socket.on('translation-started', ({ peerId }) => {
      // Peer started translation - start buffering their video for lip-sync immediately
      console.log(`[Meeting] Peer ${peerId} started translation, enabling video delay`);
      store.addTranslatingPeer(peerId);
    });

    socket.on('translation-stopped', ({ peerId }) => {
      // Peer stopped translating, remove from delay list
      console.log(`[Meeting] Peer ${peerId} stopped translation, disabling video delay`);
      store.removeTranslatingPeer(peerId);
    });

    socket.on('translated-audio', ({ peerId, audio }) => {
      // Play translated audio from other participants
      playTranslatedAudio(audio);
    });

    socket.on('force-mute', () => {
      if (localStreamRef.current) {
        localStreamRef.current.getAudioTracks().forEach((t) => (t.enabled = false));
      }
      store.setAudioEnabled(false);
    });

    socket.on('kicked', () => {
      setError('You have been removed from the meeting');
      cleanup();
    });

    socket.on('meeting-ended', () => {
      store.setStatus('ended');
    });
  };

  const setupMediasoup = async (stream) => {
    console.log('=== setupMediasoup STARTED ===');
    try {
      await mediasoup.loadDevice();
      console.log('Device loaded');

      // Produce audio
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioProducerRef.current = await mediasoup.produce(audioTrack, { isScreenShare: false });
      }

      // Produce video - IMPORTANT: Clone the track before giving it to mediasoup!
      // This prevents mediasoup.replaceTrack() from ending the original camera track,
      // which the video effects processor needs to stay alive.
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        const clonedTrack = videoTrack.clone();
        console.log('[MeetingPage] Cloned video track for mediasoup:',
          'original:', videoTrack.id, 'clone:', clonedTrack.id);
        videoProducerRef.current = await mediasoup.produce(clonedTrack, { isScreenShare: false });
      }
      console.log('Audio and video produced');

      // Get existing producers and consume them
      const { producers } = await emitAsync('get-producers');
      console.log('Got existing producers:', producers);
      for (const producer of producers) {
        try {
          console.log('Consuming producer:', producer.producerId, producer.kind);
          const consumer = await mediasoup.consume(producer.producerId);
          console.log('Consumer created:', consumer.id, consumer.kind);
          addRemoteTrack(producer.peerId, consumer.track, consumer.kind);
        } catch (err) {
          console.error('Failed to consume existing producer:', err);
        }
      }
    } catch (err) {
      console.error('Failed to setup mediasoup:', err);
    }
  };

  const addRemoteTrack = (peerId, track, kind) => {
    setRemoteStreams((prev) => {
      const next = new Map(prev);
      let stream = next.get(peerId);
      if (!stream) {
        stream = new MediaStream();
        next.set(peerId, stream);
      }
      // Remove existing track of same kind
      stream.getTracks().forEach((t) => {
        if (t.kind === kind) stream.removeTrack(t);
      });
      stream.addTrack(track);
      return next;
    });
  };

  const cleanup = () => {
    stopAudioCapture();
    stopPlayback();
    stopAudioStreamProcessing(); // Stop AI noise suppression
    stopSTTProcessing(); // Stop STT processing (includes VAD)
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
    }
    mediasoup.cleanup();
    disconnectSocket();
    store.reset();
  };

  // Control handlers
  const handleToggleAudio = useCallback(() => {
    const newState = !store.audioEnabled;
    console.log(`[Toggle] Audio: ${store.audioEnabled} -> ${newState}`);
    if (localStreamRef.current) {
      const tracks = localStreamRef.current.getAudioTracks();
      console.log(`[Toggle] Audio tracks:`, tracks.map(t => ({ id: t.id, enabled: t.enabled, readyState: t.readyState })));
      tracks.forEach((t) => {
        t.enabled = newState;
        console.log(`[Toggle] Set audio track ${t.id} enabled=${newState}`);
      });
    }
    store.setAudioEnabled(newState);
    getSocket()?.emit('toggle-audio', { enabled: newState });
  }, [store.audioEnabled]);

  const handleToggleVideo = useCallback(() => {
    const newState = !store.videoEnabled;
    console.log(`[Toggle] Video: ${store.videoEnabled} -> ${newState}`);
    if (localStreamRef.current) {
      const tracks = localStreamRef.current.getVideoTracks();
      console.log(`[Toggle] Video tracks:`, tracks.map(t => ({ id: t.id, enabled: t.enabled, readyState: t.readyState })));
      tracks.forEach((t) => {
        t.enabled = newState;
        console.log(`[Toggle] Set video track ${t.id} enabled=${newState}`);
      });
    }
    store.setVideoEnabled(newState);
    getSocket()?.emit('toggle-video', { enabled: newState });
  }, [store.videoEnabled]);

  const handleToggleHand = useCallback(() => {
    const newState = !store.handRaised;
    store.setHandRaised(newState);
    getSocket()?.emit('toggle-hand', { raised: newState });
  }, [store.handRaised]);

  const handleScreenShare = useCallback(async () => {
    if (store.screenSharing) {
      // Stop screen share
      const producer = mediasoup.getScreenShareProducer();
      if (producer) {
        mediasoup.closeProducer(producer.id);
      }
      store.setScreenSharing(false);
      getSocket()?.emit('stop-screen-share');
    } else {
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: true,
        });

        const videoTrack = screenStream.getVideoTracks()[0];
        await mediasoup.produce(videoTrack, { isScreenShare: true });

        videoTrack.onended = () => {
          store.setScreenSharing(false);
          getSocket()?.emit('stop-screen-share');
        };

        store.setScreenSharing(true);
        getSocket()?.emit('start-screen-share');
      } catch (err) {
        console.error('Screen share failed:', err);
      }
    }
  }, [store.screenSharing]);

  const handleLeave = useCallback(() => {
    cleanup();
    navigate('/');
  }, [navigate]);

  const handleEndMeeting = useCallback(() => {
    getSocket()?.emit('end-meeting');
    cleanup();
    navigate('/');
  }, [navigate]);

  // Loading state
  if (connecting) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography>Connecting to meeting...</Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <Typography variant="h5" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Typography
          color="primary"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          Return to Home
        </Typography>
      </Box>
    );
  }

  // Waiting room state
  if (store.status === 'waiting') {
    return <WaitingRoom meetingTitle={store.meeting?.title} />;
  }

  // Meeting ended state
  if (store.status === 'ended') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <Typography variant="h4" sx={{ mb: 2 }}>
          Meeting Ended
        </Typography>
        <Typography
          color="primary"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          Return to Home
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* Main content area */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Video grid */}
        <Box sx={{ flex: 1, p: 2 }}>
          <VideoGrid
            localStream={getOutputStream() || localStream}
            remoteStreams={remoteStreams}
            participants={store.participants}
            localParticipant={store.participant}
            audioEnabled={store.audioEnabled}
            videoEnabled={store.videoEnabled}
            translatingPeers={store.translatingPeers}
            activeSpeaker={store.activeSpeaker}
          />
        </Box>

        {/* Side panels */}
        {store.chatOpen && (
          <ChatPanel
            messages={store.messages}
            onClose={() => store.setChatOpen(false)}
          />
        )}
        {store.participantsOpen && (
          <ParticipantsPanel
            participants={store.participants}
            localParticipant={store.participant}
            isHost={store.isHost}
            waitingList={store.waitingList}
            onClose={() => store.setParticipantsOpen(false)}
          />
        )}
        {store.subtitlesEnabled && (
          <SubtitlesPanel
            transcripts={store.transcripts}
            onClose={() => store.setSubtitlesEnabled(false)}
          />
        )}
      </Box>

      {/* Controls */}
      <MeetingControls
        audioEnabled={store.audioEnabled}
        videoEnabled={store.videoEnabled}
        screenSharing={store.screenSharing}
        handRaised={store.handRaised}
        chatOpen={store.chatOpen}
        participantsOpen={store.participantsOpen}
        subtitlesOpen={store.subtitlesEnabled}
        effectsActive={effectType !== 'none'}
        noiseSuppressionActive={noiseSuppressionActive}
        vadActive={vadActive}
        onToggleEffects={() => setEffectsPanelOpen(true)}
        participantCount={store.participants.length + 1 + (store.isHost ? store.waitingList.length : 0)}
        unreadMessages={store.unreadCount}
        isHost={store.isHost}
        onToggleAudio={handleToggleAudio}
        onToggleVideo={handleToggleVideo}
        onToggleScreenShare={handleScreenShare}
        onToggleHand={handleToggleHand}
        onToggleChat={() => store.setChatOpen(!store.chatOpen)}
        onToggleParticipants={() => store.setParticipantsOpen(!store.participantsOpen)}
        onToggleSubtitles={() => store.setSubtitlesEnabled(!store.subtitlesEnabled)}
        onOpenSettings={() => store.setSettingsOpen(true)}
        onLeave={handleLeave}
        onEndMeeting={handleEndMeeting}
      />

      {/* Settings dialog */}
      <SettingsDialog
        open={store.settingsOpen}
        onClose={() => store.setSettingsOpen(false)}
      />

      {/* Video Effects Panel */}
      <VideoEffectsPanel
        open={effectsPanelOpen}
        onClose={() => setEffectsPanelOpen(false)}
      />
    </Box>
  );
}
